import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './CustomerDashboard.css';
import { logout } from '../../services/authService';
import TruckSearch from './TruckSearch';
import BookingHistory from './BookingHistory';
import CustomerProfile from './CustomerProfile';
import CustomerTracking from './CustomerTracking';

const CustomerDashboard = ({ isAuthenticated, onCustomerLogin }) => {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState(() => {
    // If not authenticated, only allow search-truck
    const savedMenu = localStorage.getItem('customerActiveMenu');
    const isAuth = localStorage.getItem('customerAuthenticated') === 'true';
    if (!isAuth) {
      return 'search-truck';
    }
    return savedMenu || 'search-truck';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [customerName, setCustomerName] = useState('Customer');
  const sidebarRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('customerActiveMenu', activeMenu);
  }, [activeMenu]);

  // Load customer name from profile
  useEffect(() => {
    const savedProfile = localStorage.getItem('customerProfile');
    if (savedProfile) {
      try {
        const profile = JSON.parse(savedProfile);
        if (profile.fullName) {
          setCustomerName(profile.fullName);
        }
      } catch (error) {
        console.error('Error loading customer profile:', error);
      }
    }
  }, []);

  // Dummy notifications data for customer
  const notifications = [
    { id: 1, message: 'Your booking request for TN10AB1234 has been confirmed', time: '10 minutes ago', type: 'success', icon: '✓' },
    { id: 2, message: 'Driver will arrive at your location in 2 hours', time: '45 minutes ago', type: 'info', icon: '🚚' },
    { id: 3, message: 'Trip #4580 has been completed successfully', time: '1 hour ago', type: 'success', icon: '✓' },
    { id: 4, message: 'Payment of ₹45,000 received for your booking', time: '2 hours ago', type: 'payment', icon: '💳' },
    { id: 5, message: 'New truck available for your route', time: '3 hours ago', type: 'info', icon: '🚛' },
  ];

  const toggleNotificationDropdown = () => {
    setNotificationDropdownOpen(!notificationDropdownOpen);
    setProfileDropdownOpen(false); // Close profile dropdown when opening notifications
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
    setNotificationDropdownOpen(false); // Close notification dropdown when opening profile
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };

    if (notificationDropdownOpen || profileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [notificationDropdownOpen, profileDropdownOpen]);

  // Only show Search Truck if not authenticated
  const menuItems = isAuthenticated ? [
    { id: 'search-truck', label: 'Search Truck', icon: '🔍' },
    { id: 'tracking', label: 'Tracking', icon: '📍' },
    { id: 'history', label: 'History', icon: '📋' },
    { id: 'profile', label: 'Profile', icon: '👤' },
  ] : [
    { id: 'search-truck', label: 'Search Truck', icon: '🔍' },
  ];

  const menuComponents = {
    'search-truck': TruckSearch,
    'tracking': CustomerTracking,
    'history': BookingHistory,
    'profile': CustomerProfile,
  };

  const handleMenuClick = (menuId) => {
    // If not authenticated and trying to access other menus, redirect to login
    if (!isAuthenticated && menuId !== 'search-truck') {
      navigate('/login');
      return;
    }
    setActiveMenu(menuId);
    setMobileMenuOpen(false);
    // Also collapse the sidebar on desktop until user opens it again
    setSidebarCollapsed(true);
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('customerActiveMenu');
    // Use authService to clear all auth data
    logout();
    window.location.href = '/';
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && mobileMenuOpen && !sidebarRef.current.contains(event.target)) {
        const mobileMenuBtn = document.querySelector('.customer-mobile-menu-btn');
        if (mobileMenuBtn && !mobileMenuBtn.contains(event.target)) {
          setMobileMenuOpen(false);
        }
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const ActiveComponent = menuComponents[activeMenu] || TruckSearch;

  return (
    <div className="customer-dashboard-container">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className={`customer-mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}
      
      {/* Top Header Bar */}
      <div className="customer-top-header">
        <div className="customer-header-left">
          <button className="customer-mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Toggle menu">
            <span className={mobileMenuOpen ? 'hamburger open' : 'hamburger'}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <h1 className="customer-page-title">
            Nettaa Logistics
            {activeMenu === 'search-truck' && (
              <span className="customer-page-subtitle"> - Search Available Trucks</span>
            )}
            {activeMenu === 'tracking' && (
              <span className="customer-page-subtitle"> - Track Your Shipment</span>
            )}
            {activeMenu === 'history' && (
              <span className="customer-page-subtitle"> - Booking History</span>
            )}
            {activeMenu === 'profile' && (
              <span className="customer-page-subtitle"> - My Profile</span>
            )}
          </h1>
        </div>
        <div className="customer-header-right">
          {isAuthenticated ? (
            <>
              <div className="customer-notifications-container" ref={notificationDropdownRef}>
                <button className="customer-notifications-btn" onClick={toggleNotificationDropdown} aria-label="Notifications">
                  <span className="customer-notification-icon">🔔</span>
                  <span className="customer-notification-badge">{notifications.length}</span>
                </button>
                {notificationDropdownOpen && (
                  <div className="customer-notification-dropdown">
                    <div className="customer-notification-dropdown-header">
                      <h3>Notifications</h3>
                      <button className="customer-mark-all-read-btn" onClick={() => setNotificationDropdownOpen(false)}>
                        Mark All Read
                      </button>
                    </div>
                    <div className="customer-notification-dropdown-list">
                      {notifications.map((notification) => (
                        <div key={notification.id} className="customer-notification-dropdown-item">
                          <span className={`customer-notification-dropdown-icon ${notification.type}`}>
                            {notification.icon}
                          </span>
                          <div className="customer-notification-dropdown-content">
                            <div className="customer-notification-dropdown-text">{notification.message}</div>
                            <div className="customer-notification-dropdown-time">{notification.time}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="customer-notification-dropdown-footer">
                      <a href="#" className="customer-view-all-notifications-link">View All Notifications →</a>
                    </div>
                  </div>
                )}
              </div>
              <div className="customer-user-profile-container" ref={profileDropdownRef}>
                <div className="customer-user-profile-header" onClick={toggleProfileDropdown}>
                  <div className="customer-user-avatar-header">C</div>
                  <div className="customer-user-info-header">
                    <span className="customer-user-name-header">{customerName}</span>
                    <span className="customer-user-role-header">Customer</span>
                  </div>
                  <button className={`customer-dropdown-arrow ${profileDropdownOpen ? 'open' : ''}`}>▼</button>
                </div>
                {profileDropdownOpen && (
                  <div className="customer-profile-dropdown">
                    <button className="customer-dropdown-item customer-logout-item" onClick={handleLogout}>
                      <span className="customer-dropdown-icon">🚪</span>
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <button className="customer-login-btn" onClick={handleLoginClick}>
              Login
            </button>
          )}
        </div>
      </div>

      {/* Left Sidebar */}
      <div 
        ref={sidebarRef}
        className={`customer-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}
      >
        <div className="customer-sidebar-header">
          <div className="customer-logo">
            <div className="customer-logo-icon">🚚</div>
          </div>
          {!sidebarCollapsed && <h2 className="customer-logo-text">Nettaa</h2>}
          <button 
            className="customer-collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        <nav className="customer-sidebar-nav">
          <ul className="customer-nav-list">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  className={`customer-nav-item ${activeMenu === item.id ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item.id)}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <span className="customer-nav-icon">{item.icon}</span>
                  {!sidebarCollapsed && <span className="customer-nav-label">{item.label}</span>}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Info at Bottom */}
        <div className="customer-sidebar-footer">
          <div className="customer-user-profile-sidebar">
            <div className="customer-user-avatar-sidebar">C</div>
            {!sidebarCollapsed && (
              <div className="customer-user-details-sidebar">
                <span className="customer-user-name-sidebar">Customer</span>
                <span className="customer-user-role-sidebar">User</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`customer-main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <ActiveComponent isAuthenticated={isAuthenticated} onCustomerLogin={onCustomerLogin} />
      </div>
    </div>
  );
};

export default CustomerDashboard;

